import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarPlus, FileSignature } from "lucide-react";
import api from "@/lib/api";
import { mockMatches } from "@/data/listings";
import { priceParts } from "@/lib/pricing";
import { useAuth, userMode } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import NavTop from "@/components/nav/nav_top";
import ChatWindow from "@/components/chat/chat_window";
import RenterNoticeModal from "@/components/chat/renter_notice_modal";
import VerifyGateModal from "@/components/gates/verify_gate_modal";
import ConsentToggle from "@/components/screening/consent_toggle";
import ViewingPanel from "@/components/viewings/viewing_panel";
import ProposeViewingModal from "@/components/viewings/propose_viewing_modal";

// The conversation. Listers get "Propose viewing" and lease actions here;
// renters get "Request viewing" (same modal, capped at three preferred
// times) plus the screening consent toggle — the only screening surface a
// renter ever sees. Ordering screening itself lives in the lister-only hub.
export default function ChatPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Opening the thread marks it read server-side; ChatWindow pings us after
  // its first successful fetch so the unread badge clears immediately.
  const { refresh: refreshNotifications } = useNotifications();
  const mode = userMode(user) || "renter";
  const [match, setMatch] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposedViewing, setProposedViewing] = useState(null);
  // Identity gate: sending a message can be refused with 403
  // VERIFICATION_REQUIRED until the one time ID check is done.
  const [verifyGate, setVerifyGate] = useState(false);
  // Mock matches are a demo-only fallback. A real signed-in user who cannot
  // load a conversation sees an honest error, never a fabricated chat.
  const canMock = !user || user.demo;

  useEffect(() => {
    let alive = true;
    setLoadError(false);
    api
      .get(`/matches/${matchId}`)
      .then((res) => alive && setMatch(res.data?.match || res.data))
      .catch(() => {
        if (!alive) return;
        if (canMock) setMatch(mockMatches.find((m) => m.id === matchId) || mockMatches[0]);
        else setLoadError(true);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, canMock]);

  // Sending lives inside ChatWindow, so the verification gate is caught here
  // at the api layer, scoped to THIS match's message endpoint and this page's
  // lifetime. On VERIFICATION_REQUIRED we open the verify modal and strip the
  // response before ChatWindow sees the failure — its catch then treats it
  // like an offline send, so the user's words stay on screen as a local
  // bubble (nothing is lost) and no generic error toast fires.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (res) => res,
      (error) => {
        const cfg = error?.config || {};
        const isSend =
          String(cfg.method || "").toLowerCase() === "post" &&
          typeof cfg.url === "string" &&
          cfg.url.includes(`/matches/${matchId}/messages`);
        const data = error?.response?.data;
        const code = data?.code || data?.detail?.code;
        if (isSend && error?.response?.status === 403 && code === "VERIFICATION_REQUIRED") {
          setVerifyGate(true);
          const softened = new Error("VERIFICATION_REQUIRED");
          softened.code = "VERIFICATION_REQUIRED";
          softened.config = cfg;
          return Promise.reject(softened);
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, [matchId]);

  if (loadError) {
    return (
      <div className="flex h-[100dvh] flex-col bg-night">
        <NavTop />
        <main className="mx-auto flex max-w-md flex-1 flex-col items-center justify-center px-6 text-center">
          <h1 className="text-lg font-semibold text-night-text">This conversation could not be loaded</h1>
          <p className="mt-2 text-sm text-night-sub">
            The match may no longer be available. Head back to your matches to continue.
          </p>
          <button
            type="button"
            onClick={() => navigate("/matches")}
            className="mt-6 rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
          >
            Back to matches
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] flex-col bg-night">
      <NavTop />

      <div className="flex items-center gap-3 border-b border-night-line bg-night-surface px-4 py-3">
        <button
          type="button"
          aria-label="Back to matches"
          onClick={() => navigate("/matches")}
          className="rounded-full p-2 text-night-sub transition hover:bg-night-tint hover:text-night-text"
        >
          <ArrowLeft size={18} />
        </button>
        {match && (
          <>
            <img src={match.listing?.photos?.[0]} alt="" className="h-10 w-10 rounded-full object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-night-text">{match.listing?.title}</p>
              <p className="truncate text-xs text-night-sub">{headerSubline(match, mode)}</p>
            </div>
          </>
        )}
        <div className="hidden items-center gap-2 sm:flex">
          {/* Both sides schedule from here: listers propose their times,
              renters request up to three preferred ones. */}
          <button
            type="button"
            onClick={() => setProposeOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-night-line bg-night-surface px-3.5 py-2 text-xs font-medium text-night-text transition hover:border-brand-primary"
          >
            <CalendarPlus size={14} className="text-brand-primary" />
            {mode === "lister" ? "Propose viewing" : "Request a viewing"}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/lease/${matchId}`)}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-primary px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-brand-primary-hover"
          >
            <FileSignature size={14} /> Start lease
          </button>
        </div>
      </div>

      {/* Mobile action strip */}
      <div className="flex gap-2 border-b border-night-line bg-night-surface px-4 py-2 sm:hidden">
        <button
          type="button"
          onClick={() => setProposeOpen(true)}
          className="flex-1 rounded-full border border-night-line py-2 text-xs font-medium text-night-text"
        >
          {mode === "lister" ? "Propose viewing" : "Request a viewing"}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/lease/${matchId}`)}
          className="flex-1 rounded-full bg-brand-primary py-2 text-xs font-semibold text-white"
        >
          Start lease
        </button>
      </div>

      {/* Renter-only: share the verified profile with this lister. The key
          remounts the toggle once the match (and its saved consent) loads. */}
      {mode === "renter" && (
        <ConsentToggle
          key={`${matchId}-${String(!!match?.screening_consent)}`}
          matchId={matchId}
          initialGranted={!!match?.screening_consent}
        />
      )}

      {/* Viewing state for this match: slot picker, waiting card or the
          confirmed banner with the calendar download */}
      <ViewingPanel matchId={matchId} mode={mode} externalViewing={proposedViewing} />

      <div className="min-h-0 flex-1">
        <ChatWindow matchId={matchId} mode={mode} onLoaded={refreshNotifications} />
      </div>

      {/* Renter beware: shown once per match on first open, renter side
          only. Listers never see it and are never blocked by it. */}
      <RenterNoticeModal matchId={matchId} mode={mode} />

      <ProposeViewingModal
        open={proposeOpen}
        matchId={matchId}
        role={mode === "lister" ? "lister" : "renter"}
        onClose={() => setProposeOpen(false)}
        onProposed={setProposedViewing}
      />

      <VerifyGateModal open={verifyGate} onClose={() => setVerifyGate(false)} />
    </div>
  );
}

// Header subline under the listing title. GET /matches/{id} carries the
// counterpart as other_party (mocks still use renter/lister_name), and the
// price goes through the shared priceParts helper — every field is guarded
// so a sparse match never renders "undefined · $NaN/mo".
function headerSubline(match, mode) {
  const counterpart =
    mode === "lister"
      ? match.other_party?.name || match.renter?.name
      : match.other_party?.name || match.listing?.lister_name;
  const { amount, unit } = priceParts(match.listing);
  const parts = [counterpart];
  if (mode !== "lister" && amount) parts.push(`$${amount.toLocaleString()}${unit}`);
  return parts.filter(Boolean).join(" · ");
}
